import { useState } from 'react';
import { CheckCircle2, Edit3 } from 'lucide-react';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import type { MostActiveLadderPath } from '../types/ladder';
import { updateStepCompletion, checkAndCompleteLadder } from '../lib/ladderUtils';

interface MostActiveLadderProps {
  ladderPath: MostActiveLadderPath;
  onStepCompleted: () => void;
  onEditClick: () => void;
}

export function MostActiveLadder({
  ladderPath,
  onStepCompleted,
  onEditClick,
}: MostActiveLadderProps) {
  const [updatingSteps, setUpdatingSteps] = useState<Set<string>>(new Set());

  const currentLadder = ladderPath.ladderPath[ladderPath.ladderPath.length - 1];

  if (!currentLadder) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>All steps completed!</p>
      </div>
    );
  }

  const handleStepToggle = async (stepId: string, currentValue: boolean) => {
    setUpdatingSteps((prev) => new Set(prev).add(stepId));

    try {
      const success = await updateStepCompletion(stepId, !currentValue);
      if (success) {
        await checkAndCompleteLadder(currentLadder.id);
        onStepCompleted();
      }
    } catch (error) {
      console.error('Error updating step:', error);
    } finally {
      setUpdatingSteps((prev) => {
        const next = new Set(prev);
        next.delete(stepId);
        return next;
      });
    }
  };

  const sortedSteps = [...currentLadder.steps].sort(
    (a, b) => b.step_number - a.step_number
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {ladderPath.ladderPath.length === 1
                ? `Ladder ${currentLadder.level_number}`
                : `Ladder ${currentLadder.level_number} - ${currentLadder.outcome_text}`
              }
            </h3>
          </div>
          {ladderPath.ladderPath.length > 1 && (
            <div className="text-sm text-gray-600">
              <span className="text-gray-400">└─  </span>
              Ladder {ladderPath.ladderPath[ladderPath.ladderPath.length - 2].level_number} - {ladderPath.ladderPath[ladderPath.ladderPath.length - 2].outcome_text}
            </div>
          )}
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        {sortedSteps.map((step) => {
          const isCurrentFocus =
            ladderPath.currentFocusStep?.id === step.id;
          const isUpdating = updatingSteps.has(step.id);

          return (
            <div
              key={step.id}
              className={`flex items-start gap-3 p-3 rounded-lg transition-all ${
                isCurrentFocus
                  ? 'bg-blue-50 border-2 border-blue-300'
                  : 'bg-white border border-gray-200'
              }`}
            >
              <div className="mt-0.5">
                <Checkbox
                  id={`step-${step.id}`}
                  checked={step.is_completed}
                  onChange={() => handleStepToggle(step.id, step.is_completed)}
                  disabled={isUpdating}
                />
              </div>
              <label
                htmlFor={`step-${step.id}`}
                className="flex-1 cursor-pointer"
              >
                <p
                  className={`text-sm mb-1 ${
                    step.is_completed
                      ? 'text-gray-500 line-through'
                      : 'text-gray-900'
                  }`}
                >
                  {step.description}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-500">
                    Step {step.step_number}
                  </span>
                  {isCurrentFocus && (
                    <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
                      Current Focus
                    </span>
                  )}
                  {step.is_completed && (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  )}
                </div>
              </label>
            </div>
          );
        })}
      </div>

      <Button variant="outline" size="sm" onClick={onEditClick}>
        <Edit3 className="w-4 h-4 mr-1" />
        Edit Ladder
      </Button>
    </div>
  );
}
